import { EntityCard } from '../shared/EntityCard';
import { Badge } from '../ui/Badge';
import { Clock } from 'lucide-react';
import { Aspiration, AspirationStatus } from '@/types/aspirations';
import { formatStatus } from '@/utils/formatters';
import { getCategoryStyle } from '@/utils/cardStyles';
import { cn } from '@/utils/cn';

interface AspirationCardProps {
  aspiration: Aspiration;
  onEdit?: (aspiration: Aspiration) => void;
  onDelete?: (aspiration: Aspiration) => void;
  onClick?: (aspiration: Aspiration) => void;
  className?: string;
  isDragging?: boolean;
  dragHandleProps?: any;
}

export function AspirationCard({
  aspiration,
  onEdit,
  onDelete,
  onClick,
  className,
  isDragging = false,
  dragHandleProps
}: AspirationCardProps) {
  const completedGoals = aspiration.linkedGoals.filter(g => g.completed).length;
  const totalGoals = aspiration.linkedGoals.length;

  // Compute progress and status dynamically from linkedGoals
  const computedProgress = totalGoals > 0
    ? Math.round((completedGoals / totalGoals) * 100)
    : 0;

  // Compute contextual status based on progress
  let contextualStatus: AspirationStatus = aspiration.status;
  if (computedProgress === 100 && totalGoals > 0) {
    contextualStatus = 'completed';
  } else if (computedProgress > 0 && aspiration.status === 'not_started') {
    contextualStatus = 'in_progress';
  }

  return (
    <EntityCard
      testId="aspiration-card"
      title={aspiration.title}
      description={aspiration.description}
      status={contextualStatus}
      statusLabel={formatStatus(contextualStatus)}
      priority={aspiration.priority}
      showPriority
      progress={computedProgress}
      deadline={aspiration.deadline}
      isCompleted={contextualStatus === 'completed'}
      className={cn('aspiration-card', className)}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      onEdit={onEdit ? () => onEdit(aspiration) : undefined}
      onDelete={onDelete ? () => onDelete(aspiration) : undefined}
      onClick={onClick ? () => onClick(aspiration) : undefined}
      badges={
        <Badge className={getCategoryStyle(aspiration.category)}>
          {aspiration.category}
        </Badge>
      }
      metadata={
        totalGoals > 0 ? (
          <div className="entity-card-meta">
            <Clock className="h-4 w-4" />
            <span>{completedGoals}/{totalGoals} goals completed</span>
          </div>
        ) : undefined
      }
    />
  );
}
